import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chip, EmptyState } from '@/components/ui';
import type { HierarchyPageVM } from './useHierarchyPage';

export interface HierarchyDetailPanelProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: the 340px detail aside (frame 28's `<aside>`), for the selected đơn vị.
 *
 * Which numbers are real, and where each comes from — this matters because the
 * mock draws several the API cannot answer:
 *
 *  - Account count for the đơn vị: REAL.
 *    `GET /organizations/{id}/provisioning-status` is per-organization, so
 *    `assignments.active` is this đơn vị's own figure.
 *  - Direct child count: REAL but only once the node is expanded — children are
 *    lazily loaded, so before the first fetch the count is unknown rather than
 *    zero, and the label says so.
 *  - "ỨNG DỤNG TRONG PHẠM VI": PARTIAL, and labelled as partial.
 *    `GET /organizations/{id}/tenants` lists `OrganizationAppScope` rows for
 *    application='ach' ONLY (the view hardcodes `ACH_APP_CODE`), and there is no
 *    endpoint listing scope rows for any other application. So an `ach` chip is
 *    shown when the đơn vị really has ACH in scope; the mock's `lms`/`sis` chips
 *    have no source and are not invented.
 *  - "TÀI KHOẢN THEO LEVEL": REAL, but org-wide by nature. An Assignment binds an
 *    account to an ORG-scoped Level, never to a particular đơn vị, so this
 *    breakdown is genuinely identical whichever node is selected — and is
 *    labelled that way rather than implied to be per-đơn vị.
 *  - Unit type ("Cấp Trường" in the mock's meta line): REAL as of EAA-1838, and
 *    reworked in the same ticket. It first read "NOT SHOWN — portal-api has no
 *    tier concept", then named the assigned `OrgTier`; both are superseded by
 *    `OrgUnitType`, so the meta line names the đơn vị's KIND. It is omitted (not
 *    shown as "—") on the rare row whose type has not loaded, rather than drawn
 *    as missing data. Authoring the vocabulary is a per-ROOT-ORG job and now
 *    happens inline in the create modal's picker (`UnitTypePicker`), not in this
 *    per-node aside.
 */
export function HierarchyDetailPanel({ vm }: HierarchyDetailPanelProps) {
    const { t } = useTranslation();
    const {
        selectedNode,
        selectedCoverageQuery,
        selectedAchScopes,
        selectedScopesQuery,
        knownOrgs,
        unitTypesById,
        activeLevels,
        levelBreakdown,
        canManage,
        beginRename,
    } = vm;

    if (!selectedNode) {
        return (
            <aside className="hz-aside">
                <div className="hz-aside-inner">
                    <EmptyState
                        title={t('console.hierarchy.detailPanel.emptyTitle')}
                        description={t('console.hierarchy.detailPanel.emptyDescription')}
                    />
                </div>
            </aside>
        );
    }

    const org = selectedNode.organization;
    const parentName = org.parent ? (knownOrgs.get(org.parent)?.name ?? null) : null;
    const activeAccounts = selectedCoverageQuery.data?.assignments.active ?? null;

    const unitType = org.unit_type ? unitTypesById.get(org.unit_type) : undefined;

    const metaParts: string[] = [];
    // EAA-1838: the mock's "Cấp Trường" clause, now backed by the đơn vị's type.
    if (unitType) {
        metaParts.push(
            unitType.is_active
                ? t('console.hierarchy.detailPanel.metaUnitTypeActive', { name: unitType.name })
                : t('console.hierarchy.detailPanel.metaUnitTypeHidden', { name: unitType.name })
        );
    }
    if (parentName) metaParts.push(t('console.hierarchy.detailPanel.metaInParent', { name: parentName }));
    else if (org.parent === null) metaParts.push(t('console.hierarchy.detailPanel.metaRoot'));
    if (activeAccounts !== null)
        metaParts.push(
            t('console.hierarchy.detailPanel.metaAccounts', {
                value: activeAccounts.toLocaleString('vi-VN'),
            })
        );
    metaParts.push(
        selectedNode.childrenLoaded
            ? t('console.hierarchy.detailPanel.metaChildrenCount', {
                  count: selectedNode.children.length,
              })
            : t('console.hierarchy.detailPanel.metaChildrenNotLoaded')
    );
    if (!org.is_active) metaParts.push(t('console.hierarchy.detailPanel.metaInactive'));

    return (
        <aside className="hz-aside">
            <div className="hz-aside-inner">
                <div className="hz-aside-header">
                    <span className="hz-aside-eyebrow">
                        {t('console.hierarchy.detailPanel.eyebrow')}
                    </span>
                    <span className="hz-aside-name">{org.name}</span>
                    <span className="hz-aside-meta">{metaParts.join(' · ')}</span>
                </div>

                <div className="hz-card">
                    <span className="hz-card-title">
                        {t('console.hierarchy.detailPanel.appsCardTitle')}
                    </span>
                    {selectedAchScopes.length > 0 && (
                        <span className="hz-chip-row">
                            <Chip>ach</Chip>
                            <span className="hz-card-note">
                                {t('console.hierarchy.detailPanel.achScopesNote', {
                                    count: selectedAchScopes.length,
                                })}
                            </span>
                        </span>
                    )}
                    {selectedScopesQuery.isSuccess && selectedAchScopes.length === 0 && (
                        <p className="hz-card-unavailable">
                            {t('console.hierarchy.detailPanel.noAchScopes')}
                        </p>
                    )}
                    <p className="hz-card-note">
                        {t('console.hierarchy.detailPanel.scopeNotInheritedNote')}
                    </p>
                    <p className="hz-card-unavailable">
                        {t('console.hierarchy.detailPanel.achOnlyNote')}
                    </p>
                </div>

                <div className="hz-card">
                    <span className="hz-card-title">
                        {t('console.hierarchy.detailPanel.levelsCardTitle')}
                    </span>
                    <p className="hz-card-note">
                        {t('console.hierarchy.detailPanel.levelsScopeNote')}
                    </p>
                    {activeLevels.map((lv) => (
                        <div key={lv.id} className="hz-level-stat-row">
                            <span>{lv.name}</span>
                            <span className="hz-level-stat-num">
                                {levelBreakdown.byLevelId[lv.id] ?? 0}
                            </span>
                        </div>
                    ))}
                    {levelBreakdown.noLevel > 0 && (
                        <div className="hz-level-stat-row hz-level-stat-row--warn">
                            <span>{t('console.hierarchy.detailPanel.noLevelRowLabel')}</span>
                            <span className="hz-level-stat-num">{levelBreakdown.noLevel}</span>
                        </div>
                    )}
                    {levelBreakdown.truncated && (
                        <p className="hz-card-note">
                            {t('console.hierarchy.detailPanel.levelSampleNote', {
                                sampleSize: levelBreakdown.sampleSize,
                                totalActive: levelBreakdown.totalActive,
                            })}
                        </p>
                    )}
                </div>

                <div className="hz-actions">
                    {/* EAA-1839: this used to point at /console/accounts — the
                        realm-wide super-admin list, which has no org filter at all
                        (hence the old "chưa lọc theo đơn vị" caveat under it). It
                        now opens the real org-scoped browser for THIS node, with
                        the đơn vị in `?org=` so the link is a shareable deep link.
                        "Gồm đơn vị con" starts ON, matching frame 34 (its chip
                        reads "Trường A – K12 + con" and its ĐƠN VỊ facet only has
                        anything to list when descendants are in scope). */}
                    <Link
                        to={`/console/hierarchy/accounts?org=${org.id}`}
                        className="hz-btn-primary"
                    >
                        {t('console.hierarchy.detailPanel.viewAccountsLink')}
                    </Link>
                    <p className="hz-card-note">
                        {t('console.hierarchy.detailPanel.viewAccountsNote')}
                    </p>
                    {/* EAA-1818: the aside footer used to duplicate the row's context
                        menu. `ORG-HIERARCHY-GUIDE.md` §29 gives it exactly two actions —
                        Xem tài khoản and Đổi tên — so "+ Đơn vị con", "Đổi loại đơn vị"
                        and "Chuyển đến…" were removed from here. NO capability is lost:
                        `HierarchyTreePanel`'s row menu already builds all three
                        ("Thêm đơn vị con", "Chuyển đến…", "Đổi loại đơn vị"), with the
                        same permission gates and the same ⌘M shortcut. The super-admin
                        hint that sat below them went too — it described buttons that are
                        no longer here, so leaving it would point at nothing. Do not
                        restore any of this without re-reading §29. */}
                    <div className="hz-btn-row">
                        {canManage && (
                            <button
                                type="button"
                                className="hz-btn-secondary"
                                onClick={() => beginRename(selectedNode)}
                            >
                                {t('console.hierarchy.detailPanel.renameButton')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </aside>
    );
}

export default HierarchyDetailPanel;
